'use client';

import { motion } from 'motion/react';

/**
 * Hand-crafted animated SVG icons for the Features section.
 * Lighter than Lottie (no runtime player), fully scalable, animate
 * on mount + on hover via parent group class.
 */

const drawPath = {
  hidden: { pathLength: 0, opacity: 0 },
  show: {
    pathLength: 1,
    opacity: 1,
    transition: { duration: 1.2, ease: [0.16, 1, 0.3, 1] },
  },
};

const fadeIn = {
  hidden: { opacity: 0, scale: 0.85 },
  show: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.6, ease: [0.2, 0.9, 0.3, 1] },
  },
};

export function DiamondIcon({ className = '' }: { className?: string }) {
  return (
    <motion.svg
      viewBox="0 0 64 64"
      fill="none"
      className={className}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.5 }}
    >
      <defs>
        <linearGradient id="diamond-g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#dcc28c" />
          <stop offset="50%" stopColor="#c9a96a" />
          <stop offset="100%" stopColor="#8c6a2b" />
        </linearGradient>
      </defs>
      <motion.path
        d="M32 10 L50 26 L32 56 L14 26 Z"
        stroke="url(#diamond-g)"
        strokeWidth="1.5"
        variants={drawPath}
      />
      <motion.path d="M14 26 L50 26" stroke="#c9a96a" strokeWidth="1" variants={drawPath} />
      <motion.path d="M32 10 L32 26" stroke="#c9a96a" strokeWidth="1" variants={drawPath} />
      <motion.path d="M23 26 L32 56" stroke="#c9a96a" strokeWidth="0.6" variants={drawPath} />
      <motion.path d="M41 26 L32 56" stroke="#c9a96a" strokeWidth="0.6" variants={drawPath} />
      {/* Shimmer highlight */}
      <motion.circle
        cx="26"
        cy="20"
        r="1.6"
        fill="#dcc28c"
        variants={fadeIn}
        transition={{ delay: 1.1 }}
      />
    </motion.svg>
  );
}

export function ChartIcon({ className = '' }: { className?: string }) {
  return (
    <motion.svg
      viewBox="0 0 64 64"
      fill="none"
      className={className}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.5 }}
    >
      <defs>
        <linearGradient id="chart-g" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor="#c9a96a" stopOpacity="0" />
          <stop offset="100%" stopColor="#c9a96a" stopOpacity="0.3" />
        </linearGradient>
      </defs>
      <motion.rect
        x="10"
        y="10"
        width="44"
        height="44"
        rx="3"
        stroke="#c9a96a"
        strokeOpacity="0.25"
        strokeWidth="1"
        variants={fadeIn}
      />
      {/* Rising trend line */}
      <motion.path
        d="M14 44 L22 40 L30 36 L38 26 L46 22 L50 16"
        stroke="url(#chart-g)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        variants={drawPath}
      />
      {/* Bars under the line */}
      {[
        { x: 14, h: 10 },
        { x: 22, h: 14 },
        { x: 30, h: 18 },
        { x: 38, h: 28 },
        { x: 46, h: 32 },
      ].map((b, i) => (
        <motion.rect
          key={i}
          x={b.x}
          y={54 - b.h}
          width="4"
          height={b.h}
          fill="url(#chart-g)"
          variants={fadeIn}
          transition={{ delay: 0.4 + i * 0.08 }}
        />
      ))}
      {/* Ascending dot */}
      <motion.circle
        cx="50"
        cy="16"
        r="2.4"
        fill="#dcc28c"
        variants={fadeIn}
        transition={{ delay: 1.2 }}
      />
    </motion.svg>
  );
}

export function GlobeIcon({ className = '' }: { className?: string }) {
  return (
    <motion.svg
      viewBox="0 0 64 64"
      fill="none"
      className={className}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.5 }}
    >
      <motion.circle
        cx="32"
        cy="32"
        r="22"
        stroke="#c9a96a"
        strokeWidth="1.2"
        variants={fadeIn}
      />
      <motion.ellipse
        cx="32"
        cy="32"
        rx="10"
        ry="22"
        stroke="#c9a96a"
        strokeWidth="1"
        variants={drawPath}
        transition={{ delay: 0.2, duration: 1 }}
      />
      <motion.line
        x1="10"
        y1="32"
        x2="54"
        y2="32"
        stroke="#c9a96a"
        strokeWidth="1"
        variants={drawPath}
        transition={{ delay: 0.5, duration: 0.8 }}
      />
      {/* Pins on GCC region */}
      {[
        { cx: 40, cy: 26 },
        { cx: 42, cy: 32 },
        { cx: 39, cy: 38 },
      ].map((p, i) => (
        <motion.circle
          key={i}
          cx={p.cx}
          cy={p.cy}
          r="2"
          fill="#dcc28c"
          variants={fadeIn}
          transition={{ delay: 0.8 + i * 0.15 }}
        />
      ))}
    </motion.svg>
  );
}
